import React from 'react';

import { cn } from '@/lib/utils';

/**
 * Les illustrations de la marque.
 *
 * Elles vivent dans `public/brand/` et non dans `src/`: ce sont 96 fichiers
 * pour 828 Ko, et les passer au bundler les ferait entrer dans le JavaScript
 * de l'application alors qu'une page n'en affiche jamais plus d'une ou deux.
 * Servies en statique, elles se chargent à la demande et se mettent en cache
 * comme n'importe quelle image.
 *
 * **Densité.** Les fichiers livrés font 161 px de large pour les états vides,
 * 176 px pour les illustrations principales. Les afficher à 200 px sur un écran
 * à densité double demanderait au navigateur un agrandissement ×2,5 fait à la
 * volée, au plus simple — et une illustration floue dit au visiteur que le
 * produit est bâclé. `scripts/redimensionner_illustrations.py` produit donc des
 * variantes ×2 et ×3, servies par `srcset`; le navigateur prend celle qui
 * correspond à son écran.
 *
 * Ces variantes n'inventent aucun détail: c'est du ré-échantillonnage, pas de
 * la reconstruction. La vraie réponse reste un export ×2 depuis la source
 * vectorielle. La vectorisation automatique a été essayée et écartée — ces
 * dessins portent des dégradés, que la quantification en aplats écrase.
 */

const RACINE = '/brand';

/** Largeur native des fichiers, en pixels. Source: le manifeste du kit. */
const LARGEUR_NATIVE = {
  empty: 161,
  main: 176,
  decorative: 176,
};

const DOSSIERS = {
  empty: 'illustrations/empty_states',
  main: 'illustrations/main',
  decorative: 'illustrations/decorative',
};

/**
 * Les états vides disponibles. Le nom est celui du métier, pas du fichier:
 * une page demande « aucun client », pas « aucun_client.png ».
 */
export const ETATS_VIDES = {
  donnee: 'aucune_donnee',
  resultat: 'aucun_resultat',
  client: 'aucun_client',
  zone: 'aucune_zone',
  facture: 'aucune_facture',
  mission: 'aucune_mission',
  alerte: 'aucune_alerte',
  ajour: 'tout_est_a_jour',
};

export const ILLUSTRATIONS = {
  transport: 'transport_et_livraison',
  suivi: 'suivi_en_temps_reel',
  rapports: 'analyse_et_rapports',
  documents: 'documents_et_factures',
  conformite: 'conformite_et_securite',
  clients: 'clients_et_partenaires',
  zones: 'zones_geographiques',
  carburant: 'controle_carburant',
  administration: 'administration',
  chauffeur: 'chauffeur',
  commandes: 'commandes',
  flotte: 'flotte',
  integrations: 'integrations',
  location: 'location',
  recherche: 'recherche',
  repartition: 'repartition',
  station: 'station',
  stock: 'stock',
  tresorerie: 'tresorerie',
};

export const Illustration = ({
  /** Nom de fichier sans extension, ou clé de `ETATS_VIDES` / `ILLUSTRATIONS`. */
  name,
  /** `empty` | `main` | `decorative` */
  variant = 'empty',
  /** Texte alternatif. Vide par défaut: une illustration d'état vide répète
      ce que le titre dit déjà, et l'annoncer ferait doublon au lecteur d'écran. */
  alt = '',
  largeurMax,
  className,
  ...rest
}) => {
  const table = variant === 'empty' ? ETATS_VIDES : ILLUSTRATIONS;
  const fichier = table[name] || name;
  const dossier = DOSSIERS[variant] || DOSSIERS.empty;
  const natif = LARGEUR_NATIVE[variant] || 176;
  // Deux fois la taille native au maximum: au-delà, même la variante ×3 finit
  // par être agrandie et l'on retombe dans le flou qu'on cherchait à éviter.
  const plafond = Math.min(Number(largeurMax) || natif * 2, natif * 2);
  const base = `${RACINE}/${dossier}/${fichier}`;

  return (
    <img
      src={`${base}.png`}
      srcSet={`${base}.png 1x, ${base}@2x.png 2x, ${base}@3x.png 3x`}
      alt={alt}
      aria-hidden={alt ? undefined : 'true'}
      loading="lazy"
      decoding="async"
      style={{ maxWidth: `${plafond}px` }}
      className={cn('h-auto w-full select-none', className)}
      {...rest}
    />
  );
};

/**
 * Une icône du kit, pour les cas où aucune icône vectorielle n'existe.
 *
 * À n'utiliser qu'en dernier recours: ces PNG ne suivent ni la couleur du
 * texte ni le thème sombre, là où les icônes vectorielles de l'application le
 * font toutes seules. Elles ont leur place dans la navigation, où le dessin
 * est justement propre à la marque, pas dans un bouton.
 */
export const AppIcon = ({ name, size = 24, alt = '', className, ...rest }) => (
  <img
    src={`${RACINE}/icons/${name}.png`}
    alt={alt}
    aria-hidden={alt ? undefined : 'true'}
    width={size}
    height={size}
    loading="lazy"
    decoding="async"
    className={cn('select-none object-contain', className)}
    {...rest}
  />
);

export default Illustration;
